import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [nosotrosOpen, setNosotrosOpen] = useState(false);
  const [mobileNosotrosOpen, setMobileNosotrosOpen] = useState(false);
  const nosotrosRef = useRef<HTMLDivElement>(null);
  const nosotrosTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setNosotrosOpen(false);
    setMobileNosotrosOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { name: 'Especialidades Médicas', path: '/especialidades-medicas' },
  ];

  const navItemsAfter = [
    { name: 'Turnos', path: '/turnos' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const nosotrosItems = [
    { name: 'Quiénes Somos', path: '/quienes-somos' },
    { name: '¿Por Qué Renova?', path: '/por-que-renova' },
  ];

  const showTransparent = !scrolled;

  // Navbar invisible hasta hacer scroll (en todas las páginas)
  const navHidden = !scrolled && !mobileMenuOpen;

  const isActive = (path: string) => location.pathname === path;
  const isNosotrosActive = nosotrosItems.some((item) => location.pathname === item.path);

  const handleNosotrosEnter = () => {
    if (nosotrosTimeout.current) clearTimeout(nosotrosTimeout.current);
    setNosotrosOpen(true);
  };

  const handleNosotrosLeave = () => {
    nosotrosTimeout.current = setTimeout(() => {
      setNosotrosOpen(false);
    }, 150);
  };

  const socials = [
    {
      icon: 'ri-facebook-fill',
      label: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61588432989584',
      hoverClass: 'hover:text-[#1877F2]',
    },
    {
      icon: 'ri-instagram-line',
      label: 'Instagram',
      url: 'https://www.instagram.com/centrorenovamerlo?igsh=MXF1bTZieXIycnd6Ng==',
      hoverClass: 'hover:text-white',
    },
    {
      icon: 'ri-tiktok-line',
      label: 'TikTok',
      url: 'https://www.tiktok.com/@cmrenovamerlo',
      hoverClass: 'hover:text-white',
    },
  ];

  return (
    <>
      {/* Top Bar */}
      <motion.div
        initial={false}
        animate={{
          y: navHidden ? -50 : 0,
          opacity: navHidden ? 0 : 1,
          filter: navHidden ? 'blur(8px)' : 'blur(0px)',
        }}
        transition={{
          type: 'spring',
          stiffness: 120,
          damping: 20,
          mass: 0.8,
          opacity: { duration: 0.4, ease: 'easeOut' },
          filter: { duration: 0.35 },
        }}
        className={`fixed top-0 left-0 right-0 z-[60] ${
          navHidden ? 'pointer-events-none' : ''
        } ${showTransparent ? 'bg-black/20 backdrop-blur-sm' : 'bg-brand-green-900'}`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-brand-gold-400 text-xs" />
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Juncal+253,+Merlo,+Buenos+Aires,+Argentina"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 text-xs font-medium tracking-wide hover:text-brand-gold-400 transition-colors duration-300 cursor-pointer"
            >
              Juncal 253, Merlo, Buenos Aires
            </a>
          </div>
          <div className="flex items-center gap-2">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label={social.label}
                className={`w-7 h-7 flex items-center justify-center rounded-md text-white/70 transition-all duration-300 cursor-pointer ${
                  social.label === 'Instagram' ? `${social.hoverClass} group` : social.hoverClass
                }`}
              >
                {social.label === 'Instagram' ? (
                  <span className="relative w-7 h-7 flex items-center justify-center rounded-md overflow-hidden">
                    <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] rounded-md" />
                    <i className={`${social.icon} text-sm relative z-10 group-hover:text-white`} />
                  </span>
                ) : social.label === 'TikTok' ? (
                  <span className="relative w-7 h-7 flex items-center justify-center rounded-md overflow-hidden group">
                    <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black rounded-md" />
                    <i className={`${social.icon} text-sm relative z-10 group-hover:text-white`} />
                  </span>
                ) : (
                  <i className={`${social.icon} text-sm`} />
                )}
              </a>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Desktop + Mobile Navigation Bar */}
      <motion.nav
        initial={false}
        animate={{
          y: navHidden ? -60 : 0,
          opacity: navHidden ? 0 : 1,
          filter: navHidden ? 'blur(8px)' : 'blur(0px)',
        }}
        transition={{
          type: 'spring',
          stiffness: 120,
          damping: 20,
          mass: 0.8,
          delay: navHidden ? 0 : 0.05,
          opacity: { duration: 0.4, ease: 'easeOut', delay: navHidden ? 0 : 0.05 },
          filter: { duration: 0.35, delay: navHidden ? 0 : 0.05 },
        }}
        className={`fixed left-0 right-0 z-50 ${
          navHidden ? 'pointer-events-none' : ''
        } ${
          navHidden ? 'top-0' : 'top-9'
        } ${
          showTransparent
            ? 'bg-transparent py-3 md:py-5'
            : 'bg-white/95 backdrop-blur-md shadow-lg py-2 md:py-3'
        }`}
        style={{ transition: 'background-color 0.5s ease, padding 0.5s ease, box-shadow 0.5s ease, top 0.4s ease' }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity duration-300"
          >
            <img
              src="https://wsrv.nl/?url=cmrenova.com.ar/logo.png&w=400&output=webp"
              alt="RENOVA Logo"
              className="h-[100px] md:h-[120px] w-auto"
              width={240}
              height={120}
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            {/* Especialidades Médicas */}
            {navItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
              >
                <Link
                  to={item.path}
                  className={`relative text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap group ${
                    showTransparent
                      ? 'text-white/90 hover:text-white'
                      : 'text-gray-700 hover:text-brand-green-600'
                  } ${isActive(item.path) ? (showTransparent ? 'text-brand-gold-400' : 'text-brand-green-600') : ''}`}
                >
                  {item.name}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-brand-gold-400 transition-all duration-300 group-hover:w-full ${
                      isActive(item.path) ? 'w-full' : 'w-0'
                    }`}
                  ></span>
                </Link>
              </motion.div>
            ))}

            {/* Nosotros Dropdown */}
            <div
              ref={nosotrosRef}
              className="relative"
              onMouseEnter={handleNosotrosEnter}
              onMouseLeave={handleNosotrosLeave}
            >
              <button
                className={`relative text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap group flex items-center gap-1 ${
                  showTransparent
                    ? 'text-white/90 hover:text-white'
                    : 'text-gray-700 hover:text-brand-green-600'
                } ${isNosotrosActive ? (showTransparent ? 'text-brand-gold-400' : 'text-brand-green-600') : ''}`}
              >
                Nosotros
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${
                    nosotrosOpen ? 'rotate-180' : ''
                  }`}
                ></i>
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-brand-gold-400 transition-all duration-300 group-hover:w-full ${
                    isNosotrosActive ? 'w-full' : 'w-0'
                  }`}
                ></span>
              </button>

              <AnimatePresence>
                {nosotrosOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="py-2">
                      {nosotrosItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center px-5 py-3 text-sm font-medium transition-all duration-200 cursor-pointer ${
                            isActive(item.path)
                              ? 'bg-brand-green-50 text-brand-green-600'
                              : 'text-gray-700 hover:bg-brand-green-50/60 hover:text-brand-green-600'
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Turnos & Contacto */}
            {navItemsAfter.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 3), duration: 0.5 }}
              >
                <Link
                  to={item.path}
                  className={`relative text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap group ${
                    showTransparent
                      ? 'text-white/90 hover:text-white'
                      : 'text-gray-700 hover:text-brand-green-600'
                  } ${isActive(item.path) ? (showTransparent ? 'text-brand-gold-400' : 'text-brand-green-600') : ''}`}
                >
                  {item.name}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-brand-gold-400 transition-all duration-300 group-hover:w-full ${
                      isActive(item.path) ? 'w-full' : 'w-0'
                    }`}
                  ></span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Desktop CTA Button */}
          <motion.div
            className="hidden md:block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <a
              href="https://www.misturnosmovil.com.ar/SistemaMisTurnosMovil/?clienteId=318"
              target="_blank"
              rel="noopener noreferrer"
              className="group px-6 py-3 bg-brand-green-900 text-white text-sm font-semibold rounded-full hover:bg-brand-green-600 hover:shadow-lg hover:shadow-brand-green-600/30 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              <i className="ri-calendar-todo-line text-base transition-transform duration-300 group-hover:rotate-12"></i>
              Agendar Turno
              <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
          </motion.div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
              showTransparent ? 'text-white' : 'text-gray-800'
            }`}
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-4 flex flex-col justify-between">
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 origin-center ${
                  showTransparent ? 'bg-white' : 'bg-gray-800'
                } ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}
              ></span>
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 ${
                  showTransparent ? 'bg-white' : 'bg-gray-800'
                } ${mobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`}
              ></span>
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 origin-center ${
                  showTransparent ? 'bg-white' : 'bg-gray-800'
                } ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}
              ></span>
            </div>
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-72 bg-white z-50 md:hidden shadow-2xl"
          >
            <div className="flex flex-col h-full pt-20 pb-8 px-6">
              {/* Close button */}
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>

              {/* Nav Links */}
              <div className="flex flex-col gap-1">
                {/* Especialidades Médicas */}
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block text-left text-gray-800 font-medium text-base py-3.5 px-4 rounded-xl hover:bg-brand-green-50 hover:text-brand-green-600 transition-all duration-300 cursor-pointer ${
                        isActive(item.path) ? 'bg-brand-green-50 text-brand-green-600' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}

                {/* Nosotros Accordion */}
                <div>
                  <button
                    onClick={() => setMobileNosotrosOpen(!mobileNosotrosOpen)}
                    className={`w-full flex items-center justify-between text-left text-gray-800 font-medium text-base py-3.5 px-4 rounded-xl hover:bg-brand-green-50 hover:text-brand-green-600 transition-all duration-300 cursor-pointer ${
                      isNosotrosActive ? 'bg-brand-green-50 text-brand-green-600' : ''
                    }`}
                  >
                    Nosotros
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                        mobileNosotrosOpen ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <AnimatePresence>
                    {mobileNosotrosOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pb-1">
                          {nosotrosItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`flex items-center text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 cursor-pointer ${
                                isActive(item.path)
                                  ? 'bg-brand-green-50 text-brand-green-600'
                                  : 'text-gray-600 hover:bg-brand-green-50/60 hover:text-brand-green-600'
                              }`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Turnos & Contacto */}
                {navItemsAfter.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (index + 2) * 0.1 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block text-left text-gray-800 font-medium text-base py-3.5 px-4 rounded-xl hover:bg-brand-green-50 hover:text-brand-green-600 transition-all duration-300 cursor-pointer ${
                        isActive(item.path) ? 'bg-brand-green-50 text-brand-green-600' : ''
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 my-5"></div>

              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <a
                  href="https://www.misturnosmovil.com.ar/SistemaMisTurnosMovil/?clienteId=318"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="group flex w-full px-6 py-3.5 bg-brand-green-900 text-white text-sm font-semibold rounded-full text-center hover:bg-brand-green-600 hover:shadow-lg hover:shadow-brand-green-600/30 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer items-center justify-center gap-2"
                >
                  <i className="ri-calendar-todo-line text-base transition-transform duration-300 group-hover:rotate-12"></i>
                  Agendar Turno
                  <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1"></i>
                </a>
              </motion.div>

              {/* WhatsApp link */}
              <motion.a
                href="https://wa.me/5491162450002"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full px-6 py-3.5 bg-brand-green-50 text-brand-green-700 text-sm font-semibold rounded-full text-center hover:bg-brand-green-100 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp
              </motion.a>

              {/* Social Icons Mobile */}
              <motion.div
                className="flex items-center justify-center gap-3 mt-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <a
                  href="https://www.facebook.com/profile.php?id=61588432989584"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="Facebook"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-[#1877F2] hover:text-white transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg" />
                </a>
                <a
                  href="https://www.instagram.com/centrorenovamerlo?igsh=MXF1bTZieXIycnd6Ng=="
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg" />
                </a>
                <a
                  href="https://www.tiktok.com/@cmrenovamerlo"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="TikTok"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-tiktok-line text-lg" />
                </a>
              </motion.div>

              {/* Bottom info */}
              <motion.div
                className="mt-auto text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <p className="text-xs text-gray-400">
                  Lun-Vie: 7AM-8PM &bull; Sáb: 8AM-4PM
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
